import { ITrack } from "@/interfaces/ITrack"
import { ExplorerItem, ExplorerItemId, Point, Segment } from "@/types/types"
import { PeaksInstance, Point as PeaksPoint, PointOptions, Segment as PeaksSegment, SegmentOptions } from "peaks.js"
import {
  getZoomview, PeaksInstanceType, WAVEFORM_ZOOMVIEW_TYPE_NAME, WaveformZoomviewType
} from "@/features/AudioWorkstation/utils/audio-editor-utils.ts"
import fetchInstance from "@/services/fetch";
import styles from "@/features/AudioWorkstation/components/AudioWorkstation.module.scss";
import { getCookie } from "cookies-next";
import { THEME_MODE } from "@/enum/cookie.name.enum.ts";
import { THEME_DARK } from "@/theme/theme.ts";
import { audioEditorPeaksInstanceAtom } from "@/state/jotai/atom/audioEditorPeaksInstanceAtom.ts";
import { editingSegmentAtom } from "@/state/jotai/atom/editingSegmentAtom.ts";
import { jotaiStore } from "@/state/jotai/jotaiStore.ts";
import { swatches } from "@/common/components/ColorSwatches/swatches.ts";
import { playingSegmentAtom } from "@/state/jotai/atom/playingSegmentAtom.ts";
import { currentExplorerItemAtom } from "@/state/jotai/atom/currentExplorerItemAtom.ts";
import { listPeaksInstanceAtom } from "@/state/jotai/atom/listPeaksInstanceAtom.ts";
import { AppHashTag } from "@/enum/AppHashTag.ts";
import { EXPLORER_ROOT } from "@/features/Explorer/Explorer.tsx";
import { GridApiPro } from "@mui/x-data-grid-pro";
import { routes } from "@/routes.ts";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { URLSearchParamKeys } from "@/enum/URLSearchParamKeys.ts";
import { buildUrlWithParams } from "@/utils/utils.ts";

export const convertToAddablePoint = (point: Point) => {
  return {
    id: point.id,
    time: point.time,
    editable: true,
    color: point.color,
    labelText: point.labelText,
    info: point.info,
  }
}

export const convertPeaksPointsToAddablePoints = (
  points: PointOptions[],
): { [key: string]: PointOptions } => {
  return Object.keys(points).reduce(
    (result, key) => {
      const point = points[key as unknown as number]
      const id = point.id as string

      result[id] = {
        id,
        time: point.time,
        editable: true,
        color: point.color,
        labelText: point.labelText,
        info: point.info,
      }

      return result
    },
    {} as { [key: string]: PointOptions },
  )
}

export const convertToAddableSegments = (
  segments: SegmentOptions[],
): { [key: string]: SegmentOptions } => {
  return Object.keys(segments).reduce(
    (result, key) => {
      const segment = segments[key as unknown as number]

      const id = segment.id as string

      result[id] = convertToNewSerializableSegment(segment)

      return result
    },
    {} as { [key: string]: SegmentOptions },
  )
}

export const convertToNewSerializableSegment = (
  segment: SegmentOptions,
) => {
  return {
    id: segment.id,
    editable: segment.editable,
    startTime: segment.startTime,
    endTime: segment.endTime,
    color: segment.color,
    labelText: segment.labelText,
    description: segment.description,
  }
}

export const convertExistingToSerializableSegment = (
  segment: SegmentOptions,
) => {
  return {
    editable: segment.editable,
    startTime: +segment.startTime,
    endTime: +segment.endTime,
    color: segment.color,
    labelText: segment.labelText,
    description: segment.description,
  }
}

export const WAVEFORM_CURSOR_INDICATOR_LINE_ID = 'segment-cutting-indicator-line'
export const removeSegmentCuttingIndicatorLine = () => {
  document.getElementById(WAVEFORM_CURSOR_INDICATOR_LINE_ID)?.remove()
}

export const createSegmentCuttingIndicatorLine = (container: HTMLDivElement) => {
  const themeMode = getCookie(THEME_MODE) as string
  const line = document.createElement('div');
  line.id = WAVEFORM_CURSOR_INDICATOR_LINE_ID
  line.classList.add(
    themeMode === THEME_DARK
      ? styles.ZoomviewContainerCuttingLine
      : styles.ZoomviewContainerCuttingLineLightTheme
  );
  container.appendChild(line);

  return line
}

export const getListPeaks = (): PeaksInstanceType | null => {
  return jotaiStore.get(listPeaksInstanceAtom)
}

export const getAudioEditorPeaks = (): PeaksInstanceType | null => {
  return jotaiStore.get(audioEditorPeaksInstanceAtom)
}

export const zoomAudioEditorSegment = (segment?: Segment) => {
  const editingSegment = segment
  if (!editingSegment) {
    throw Error("Warning: editingSegment is not defined")
  }
  const peaksInstance = getAudioEditorPeaks()
  if (!peaksInstance) {
    throw Error("Warning: peaksInstance is not defined")
  }

  const view = peaksInstance.views.getView(WAVEFORM_ZOOMVIEW_TYPE_NAME) as WaveformZoomviewType
  const duration = editingSegment.endTime - editingSegment.startTime;

  view.setZoom({ seconds: duration + 1 });
  view.setStartTime(editingSegment.startTime - .5);
}

export const zoomIntoSegment = (peaksInstance: PeaksInstanceType, segment?: Segment) => {
  const editingSegment = segment
  if (!editingSegment) {
    throw Error("Warning: editingSegment is not defined")
  }
  if (!peaksInstance) {
    throw Error("Warning: peaksInstance is not defined")
  }

  const view = peaksInstance.views.getView(WAVEFORM_ZOOMVIEW_TYPE_NAME) as WaveformZoomviewType
  const duration = editingSegment.endTime - editingSegment.startTime;

  view.setZoom({ seconds: duration + 1 });
  view.setStartTime(editingSegment.startTime - .5);
}

export const deletePointFromPeaks = (id: string) => {
  const peaksInstance = getAudioEditorPeaks()

  peaksInstance?.points.removeById(id)
}

export const addPointToPeaks = (point: Point | PeaksPoint) => {
  const peaksInstance = getAudioEditorPeaks()

  peaksInstance?.points.add(point)
}

export const updatePointInPeaks = (point: Point | PeaksPoint) => {
  const peaksInstance = getAudioEditorPeaks()

  // const peaksPoint = peaksInstance?.points.getPoint(point.id as string)
  peaksInstance?.points.removeById(point.id as string)
  peaksInstance?.points.add(point)
}

export const SEGMENT_DUMMY_ID = `segment-dummy`;
export const getSegmentDummyId = (tabId: string) => {
  return `${SEGMENT_DUMMY_ID}-${tabId}`
}

export const getRandomColor = () => {
  const randomIndex = Math.floor(Math.random() * swatches.length);
  return swatches[randomIndex];
};

export const findPreviousSegment = (peaks: PeaksInstance, startTime: number): Segment | undefined => {
  const segments = peaks.segments.getSegments();
  let previousSegment: Segment | undefined = undefined;

  segments.forEach((segment: Segment) => {
    if (segment.startTime < startTime) {
      if (!previousSegment || segment.startTime > previousSegment.startTime) {
        previousSegment = segment;
      }
    }
  });

  return previousSegment;
};

export const getNextColor = (currentColor: string): string => {
  const currentIndex = swatches.indexOf(currentColor);  // Zorg dat currentColor van type 'string' is
  if (currentIndex === -1) {
    return getRandomColor();
  }

  // Als de huidige kleur gevonden wordt, geef de volgende kleur in de array terug
  // Als het de laatste kleur is, ga dan terug naar het begin van de array
  const nextIndex = (currentIndex + 1) % swatches.length;

  return swatches[nextIndex];
};

export const removeDummySegments = () => {
  const peaksInstance = getAudioEditorPeaks()
  if (!peaksInstance) {
    return;
  }

  peaksInstance.segments.getSegments().forEach(segment => {
    if (typeof segment.id === 'string' && segment.id.includes(SEGMENT_DUMMY_ID)) {
      console.log(segment.id);
      peaksInstance.segments.removeById(segment.id)
    }
  })
}

export const upsertSegmentInPeaks = (segment: Segment | PeaksSegment): Promise<Segment[]> => {
  const listPeaks = getListPeaks()
  const peaksInstance = getAudioEditorPeaks()
  if (!peaksInstance) {
    return new Promise(resolve => resolve)
  }

  return new Promise<Segment[]>((resolve) => {
    const peaksSegment = peaksInstance.segments.getSegment(segment.id as string)
    if (!peaksSegment) {
      peaksInstance.segments.add(segment)
      listPeaks?.segments.add(segment)
      return
    }

    if (!segment) {
      return
    }
    const updatedSegment = convertExistingToSerializableSegment(segment)
    const listPeaksSegment = listPeaks?.segments.getSegment(segment.id as string)

    peaksSegment.update({
      ...updatedSegment,
    })
    listPeaksSegment?.update({
      ...updatedSegment,
    })

    resolve(peaksInstance.segments.getSegments() as Segment[])
  })
}

export const updateSegmentInPeaks = (segment: Segment): Promise<Segment[]> => {
  const listPeaks = getListPeaks()
  const peaksInstance = getAudioEditorPeaks()
  if (!peaksInstance) {
    return new Promise(resolve => resolve)
  }

  return new Promise<Segment[]>((resolve) => {
    const peaksSegment = peaksInstance.segments.getSegment(segment.id as string)
    if (!segment || !peaksSegment) {
      return
    }

    const listPeaksSegment = listPeaks?.segments.getSegment(segment.id as string)

    // peaksInstance.segments.removeById(segment.id as string)
    // peaksInstance.segments.add(segment)

    const updatedSegment = convertExistingToSerializableSegment(segment)

    peaksSegment.update({
      ...updatedSegment,
    })

    listPeaksSegment?.update({
      ...updatedSegment,
    })

    resolve(peaksInstance.segments.getSegments() as Segment[])
  })
}

export const getSegmentFromPeaks = (id: string): Segment => {
  const peaksInstance = getAudioEditorPeaks()
  if (!peaksInstance) {
    throw Error("Peaks not initialized (getSegmentFromPeaks)")
  }

  return peaksInstance?.segments.getSegment(id) as Segment
}

export const deleteSegmentFromPeaks = (id: string): Promise<Segment[]> => {
  const peaksInstance = getAudioEditorPeaks()
  if (!peaksInstance) {
    return new Promise(resolve => resolve)
  }

  return new Promise<Segment[]>((resolve) => {
    if (!peaksInstance.segments.getSegment(id)) {
      return
    }
    peaksInstance.segments.removeById(id)

    const listPeaks = getListPeaks()
    listPeaks?.segments.removeById(id)

    resolve(peaksInstance.segments.getSegments() as Segment[])
  })
}

/*export const apiCreatePoint = (track: ITrack, time: number | null, title: string, info?: string) => {
  const point = {
    time,
    info,
    labelText: title,
  }

  return fetchInstance.post(`/track/${track.id}/point/create`, point)
}

export const apiUpdatePoint = (
  track: ITrack,
  point: Point | PeaksPoint,
  time: number,
  title: string,
  info?: string,
) => {
  const updatedPoint = {
    time: time,
    labelText: title,
    info: info,
  }

  return fetchInstance.post(`/track/${track.id}/point/${point.id}/update`, updatedPoint)
}

export const apiDeletePoint = (point: Point | PeaksPoint, track: ITrack) => {
  return fetchInstance.delete(`/track/${track.id}/point/${point.id}/delete`)
}

export const apiDeleteSegment = (segment: Segment | PeaksSegment, track: ITrack) => {
  return fetchInstance.delete(`/track/${track.id}/segment/${segment.id}/delete`)
}*/

export const apiExportSegments = (
  explorerItemId: ExplorerItemId,
  explorerItemParentId: string,
  uniqueTabId: string,
  amountOfSegments: number
) => {
  return fetchInstance.post(`/track/segments/export`, {
    explorerItemId,
    explorerItemParentId,
    uniqueTabId,
    amountOfSegments,
  })
}

export const apiExportSegment = (
  segment: Segment,
  track: ITrack,
  uniqueTabId: string,
) => {
  return fetchInstance.post(`/track/${track.id}/segment/${segment.id}/export`, {
    // start: segment._startTime,
    // end: segment._endTime,
    // audio: track,
    uniqueTabId,
  })
}

export const apiExportAndDownloadSegment = async (segment: Segment, track: ITrack) => {
  const result = await fetchInstance.post(
    `/track/${track.id}/segment/${segment.id}/download-export`,
    {
      start: segment.startTime,
      end: segment.endTime,
      audio: track,
    },
  );

  return result.blob();
}

export const apiDeleteSelectedAudio = (segment: Segment | PeaksSegment, track: ITrack) => {
  return fetchInstance.delete(`/track/${track.id}/segment-audio/${segment.id}/delete`)
}

export const toggleEditSegmentMode = (segmentId: string) => {
  const peaksInstance = getAudioEditorPeaks()
  if (!peaksInstance) {
    console.log("Peaks not initialized (toggleEditSegmentMode)")
    return
  }

  const segment = peaksInstance.segments.getSegment(segmentId) as Segment
  jotaiStore.set(editingSegmentAtom, segment)

  peaksInstance.segments.removeById(segmentId)

  const updatedSegment = {
    id: segment.id,
    startTime: segment.startTime,
    endTime: segment.endTime,
    editable: !segment.editable,
    color: segment.color,
    labelText: segment.labelText,
    userAttributes: segment.userAttributes,
  }

  peaksInstance.segments.add(updatedSegment)

  const zoomview = getZoomview(peaksInstance)
  if (!zoomview) {
    throw Error("Zoomview not defined.")
  }
  const segmentShape = zoomview._segmentsLayer._segmentShapes[segment.id as unknown as number]
  if (segmentShape) {
    // Move segment to top
    segmentShape._moveToTop()
  }
}

export const disablePeaksEditSegmentMode = () => {
  // @TODO: this function is too optimistic about validity of segments,
  // for instance: segment end time can be a lower number than the segment start time, which is not possible in Peaks.js
  // : fix the endTime: value
  const peaksInstance = getAudioEditorPeaks()
  if (!peaksInstance) {
    console.log("Peaks not initialized (disablePeaksEditSegmentMode)")
    return
  }

  jotaiStore.set(editingSegmentAtom, null)

  peaksInstance.segments.getSegments().forEach(segment => {
    segment.update({
      ...convertExistingToSerializableSegment(segment),
      editable: false,
    })
  })
}

export const AUDIO_EDITOR_CLASSNAME = 'audio-editor';
export const KONVAJS_CONTENT_CLASSNAME = 'konvajs-content';

export const getZoomviewKonvaContainer = (peaks: PeaksInstanceType): HTMLDivElement | null => {
  return getZoomview(peaks)?._container?.querySelector(`.${KONVAJS_CONTENT_CLASSNAME}`) || null
}

export const hideCommentsContainer = (peaksInstance: PeaksInstanceType, /*_isSmallScreen: boolean*/) => {
  const konvaJsContent = document
    .querySelector(".zoomview-container .konvajs-content") as HTMLDivElement;

  if (!peaksInstance || !konvaJsContent) {
    return
  }

  // konvaJsContent.style.height = isSmallScreen ? "130px" : "165px";

  const canvases = konvaJsContent
    .querySelectorAll("canvas") as NodeListOf<HTMLCanvasElement>;

  canvases.forEach((canvas, index) => {
    if (index === 2) {
      canvas.style.top = "0px";
      canvas.style.bottom = "0px";
      canvas.style.opacity = '0'
      canvas.style.position = "relative"
    } else {
      // canvas.style.top = "0px";
    }
  });
}

export const showCommentsContainer = (peaksInstance: PeaksInstanceType) => {
  const konvaJsContent = document
    .querySelector(".zoomview-container .konvajs-content") as HTMLDivElement;

  if (!peaksInstance || !konvaJsContent) {
    return
  }
  // konvaJsContent.style.height = isSmallScreen ? "115px" : "165px";

  const canvases = konvaJsContent
    .querySelectorAll("canvas") as NodeListOf<HTMLCanvasElement>;

  canvases.forEach((canvas, index) => {
    if (index === 2) {
      canvas.style.top = "unset";
      canvas.style.bottom = "0px";
      canvas.style.opacity = '1'
      canvas.style.position = "absolute"
    } else {
      // canvas.style.top = "-24px";
    }
  });
}

export const enableKonvaContainerPointerEvents = (peaks: PeaksInstanceType) => {
  const konva = getZoomviewKonvaContainer(peaks)
  if (konva) {
    konva.style.pointerEvents = 'auto'
  }
}

export const disableKonvaContainerPointerEvents = (peaks: PeaksInstanceType) => {
  const konva = getZoomviewKonvaContainer(peaks)
  if (konva) {
    konva.style.pointerEvents = 'none'
  }
}

export const addPeaksMouseAndTouchEvents = (peaks: PeaksInstanceType) => {
  window.addEventListener(
    'mousemove',
    getZoomview(peaks)._mouseDragHandler?._mouseDragHandler._mouseMove as unknown as EventListener,
    {
      capture: false,
      passive: true
    }
  )
  window.addEventListener(
    'touchmove',
    getZoomview(peaks)._mouseDragHandler?._mouseDragHandler._mouseMove as unknown as EventListener,
    {
      capture: false,
      passive: true
    }
  )
  window.addEventListener(
    'mouseup',
    getZoomview(peaks)._mouseDragHandler?._mouseDragHandler._mouseUp as unknown as EventListener,
    {
      capture: false,
      passive: true
    }
  )
  window.addEventListener(
    'touchend',
    getZoomview(peaks)._mouseDragHandler?._mouseDragHandler._mouseUp as unknown as EventListener,
    {
      capture: false,
      passive: true
    }
  )
  window.addEventListener(
    'blur',
    getZoomview(peaks)._mouseDragHandler?._mouseDragHandler._mouseUp as unknown as EventListener,
    {
      capture: false,
      passive: true
    }
  )
}

const handleScrollToRow = (dataGridApi: GridApiPro, rowIndex: number) => {
  const rowHeight = dataGridApi?.unstable_getRowHeight(rowIndex) || 52
  const offsetTop = rowIndex * rowHeight;
  dataGridApi?.scroll({ top: offsetTop })
};

export const goToExplorerItem = (
  currentExplorerItem: ExplorerItem,
  playlistSearchParam: string | null,
  setQueryStates: (playlistId: string, trackId: string) => void,
  dataGridApi: GridApiPro,
  router: AppRouterInstance
) => {
  window.location.hash = AppHashTag.EXPLORER
  const rowIndex = dataGridApi.getRowIndexRelativeToVisibleRows(currentExplorerItem.id)

  if (window.location.pathname !== routes.DASHBOARD) {
    if (currentExplorerItem.explorerItemParentId === EXPLORER_ROOT) {
      router.push(routes.DASHBOARD);
    } else {
      router.push(
        buildUrlWithParams({
          baseUrl: routes.DASHBOARD,
          params: {
            [URLSearchParamKeys.PLAYLIST]: currentExplorerItem.explorerItemParentId,
          }
        })
      )
    }

    // @todo: find better solution
    setTimeout(() => {
      handleScrollToRow(dataGridApi, rowIndex)
    }, 500)

    return;
  }

  if (playlistSearchParam === currentExplorerItem.explorerItemParentId) {
    handleScrollToRow(dataGridApi, rowIndex)

    return;
  }

  setQueryStates(
    currentExplorerItem.explorerItemParentId === EXPLORER_ROOT ? '' : currentExplorerItem.explorerItemParentId,
    currentExplorerItem.id
  )

  handleScrollToRow(dataGridApi, rowIndex)
}

export const findFirstSegment = (track: ITrack): Segment | null => {
  const segments = track?.segments;
  const segmentsArray = Object.values(segments || {});

  segmentsArray.sort((a, b) => a.startTime - b.startTime);

  return segmentsArray.length > 0 ? segmentsArray[0] : null;
}

export const findNextSegmentByJotaiPlayingSegment = () => {
  const explorerItem = jotaiStore.get(currentExplorerItemAtom)
  const currentTrack = explorerItem?.track
  const segments = currentTrack?.segments
  const playingSegment = jotaiStore.get(playingSegmentAtom)
  const segmentsArray = Object.values(segments || {});

  segmentsArray.sort((a, b) => a.startTime - b.startTime);

  const currentSegmentIndex = segmentsArray.findIndex(
    segment => segment.id === playingSegment?.id
  );

  let nextSegment = null;
  if (currentSegmentIndex !== -1 && currentSegmentIndex < segmentsArray.length - 1) {
    nextSegment = segmentsArray[currentSegmentIndex + 1];
  }

  return nextSegment;
}

export const removePeaksMouseAndTouchEvents = (peaks: PeaksInstanceType) => {
  window.removeEventListener(
    'mousemove',
    getZoomview(peaks)._mouseDragHandler?._mouseDragHandler._mouseMove as unknown as EventListener,
    {
      capture: false,
    }
  )
  window.removeEventListener(
    'touchmove',
    getZoomview(peaks)._mouseDragHandler?._mouseDragHandler._mouseMove as unknown as EventListener,
    {
      capture: false,
    }
  )
  window.removeEventListener(
    'mouseup',
    getZoomview(peaks)._mouseDragHandler?._mouseDragHandler._mouseUp as unknown as EventListener,
    {
      capture: false,
    }
  )
  window.removeEventListener(
    'touchend',
    getZoomview(peaks)._mouseDragHandler?._mouseDragHandler._mouseUp as unknown as EventListener,
    {
      capture: false,
    }
  )
  window.removeEventListener(
    'blur',
    getZoomview(peaks)._mouseDragHandler?._mouseDragHandler._mouseUp as unknown as EventListener,
    {
      capture: false,
    }
  )
}
